import AuthService from '@root/core/src/services/auth-service';
import CaptchaService from '@root/core/src/services/captcha-service';
import Input, { TextTypes } from '@root/core/src/components/input';
import LoaderButton from '@root/core/src/components/loader-button';
import LoginHeading from '@root/login/src/components/login-heading';
import NetworkService from '@root/core/src/services/network-service';
import PropTypes from '@root/vendor/prop-types';
import QueryParamService from '@root/attribution/src/services/query-param-service';
import React, { useCallback, useEffect, useRef, useState } from '@root/vendor/react';
import RouteLink from '@root/core/src/components/route-link';
import UserSessionService from '@root/user-session/src/services/user-session-service';
import emailReturningUserConfiguration from '@root/login/src/api/email-returning-user/email-returning-user-configuration';
import environment from '@root/core/src/utils/environment';
import getAccountMatchContextConfiguration from '@root/user-session/src/api/account-match-context/get-account-match-context-configuration';
import useForm from '@root/core/src/hooks/use-form';
import useImperativeNetworkRequest from '@root/core/src/hooks/use-imperative-network-request';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import { EmailConfirmationModal } from '@root/core/src/components/modals/email-confirmation-modal';
import { emailFromToken, validateToken } from '@root/login/src/api/root-server';
import { isRequired, isValidEmail } from '@root/core/src/utils/validators';
import { shouldConfirmEmail } from '@root/core/src/utils/common-email-validator';
import { useRootId } from '@root/attribution/src/hooks/use-root-id';
import { withRouter } from '@root/core/src/components/route';

const validations = [
  isRequired('email'),
  isValidEmail('email'),
];

function useEmailToken(handleSubmission, history) {
  const [loading, setLoading] = useState(true);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!hasRun.current) {
      hasRun.current = true;
      const emailToken = QueryParamService.parseQuery(history.location.search, ['email_token']).email_token;
      const fetchData = async () => {
        try {
          const response = await emailFromToken(emailToken);
          if (response.email && response['success?']) {
            await handleSubmission(response.email);
          } else {
            setLoading(false);
          }
        } catch (error) {
          setLoading(false);
        }
      };
      if (emailToken) {
        fetchData(emailToken);
      } else {
        setLoading(false);
      }
    }
  }, [handleSubmission, history, hasRun]);
  return loading;
}

function useHandleSubmit({
  analytics,
  basePath,
  domain,
  emailReturningUser,
  history,
  getAccountMatchContext,
  rootId,
}) {
  const handleSubmission = useCallback(async (email) => {
    analytics.trackClick('CONTINUE');

    const userContext = AuthService.getCurrentUserContext();
    const result = await NetworkService.request(validateToken);

    if (userContext.email === email && result.data.valid) {
      analytics.trackEvent('ACTIVE_USER');
      UserSessionService.initializeSessionActivity();
      history.push('/');
    } else {
      if (environment.isBoomerangEnabled) {
        let captchaToken = await CaptchaService.challenge('Account_Match');
        const accountMatchResult = await getAccountMatchContext({
          email,
          captchaToken,
          rootId,
        });

        const accountMatchContext = accountMatchResult.data?.accountMatchContext;

        if (accountMatchContext?.canBoomerang && accountMatchContext?.userId) {
          analytics.trackEvent('BOOMERANG_ACCOUNT_MATCH');

          captchaToken = await CaptchaService.challenge('Email_Returning_User');
          await emailReturningUser({
            captchaToken,
            userId: accountMatchContext.userId,
            domain: environment.accountAppBaseUrl,
            rootId,
          });

          history.push(`${basePath}/login/email-link-sent`, {
            email,
          });

          return;
        }
      }

      const captchaToken = await CaptchaService.challenge('Email_Returning_User');
      await emailReturningUser({
        captchaToken,
        email,
        domain: domain === environment.quoteAppDomain ? environment.quoteAppBaseUrl : domain,
        rootId,
      });

      history.push(`${basePath}/login/email-link-sent`, {
        email,
      });
    }
  }, [analytics, basePath, domain, emailReturningUser, getAccountMatchContext, history, rootId]);
  return handleSubmission;
}

function LoginEmail({
  basePath, domain, history, logout, analytics,
}) {
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [emailReturningUser] = useImperativeNetworkRequest(emailReturningUserConfiguration);
  const [getAccountMatchContext] = useImperativeNetworkRequest(getAccountMatchContextConfiguration);

  const form = useForm({
    validations,
  });

  const rootId = useRootId();

  const completeSubmission = useHandleSubmit({
    analytics,
    basePath,
    domain,
    emailReturningUser,
    history,
    getAccountMatchContext,
    rootId,
  });
  const loading = useEmailToken(completeSubmission, history);
  const handleSignup = () => {
    analytics.trackClick('SIGN_UP');
    logout && logout();
  };

  const handleSubmissionAttempted = ({ email }) => {
    if (!emailConfirmed && shouldConfirmEmail(email)) {
      return setModalVisible(true);
    }

    setSubmitting(true);
    completeSubmission(email);
  };

  const handleConfirmEmailPressed = () => {
    setEmailConfirmed(true);
    setModalVisible(false);
    setSubmitting(true);
    completeSubmission(form.values.email);
  };

  const handleEditEmailPressed = () => {
    setEmailConfirmed(true);
    setModalVisible(false);
  };

  if (loading) {
    return null;
  } else {
    return (
      <div css={styles.wrapper}>
        {modalVisible &&
          <EmailConfirmationModal
            email={form.values.email}
            isShowing={modalVisible}
            onCancel={() => setModalVisible(false)}
            onConfirmEmailPress={handleConfirmEmailPressed}
            onEditEmailPress={handleEditEmailPressed}
          />
        }
        <form
          data-testid={'email-form'}
          onSubmit={form.createSubmitHandler(handleSubmissionAttempted)}
        >
          <LoginHeading
            heading={'Welcome back.'}
            subtext={'What’s your email address? We’ll pick up where you left off.'}
          />
          <Input
            focusOnMount
            id={'email'}
            inputType={TextTypes.EMAIL}
            label={'Email'}
            {...form.getFieldProps('email')}
          />
          <LoaderButton
            isLoading={form.submitting || submitting}
            loadingText={'Sending...'}
          >
            Continue
          </LoaderButton>
        </form>
        <div css={styles.linkContainer}>
          {'New to Root? '}
          <RouteLink
            css={styles.link}
            onClick={handleSignup}
            to={'/'}
          >
            Sign up
          </RouteLink>
        </div>
      </div>
    );
  }
}

LoginEmail.propTypes = {
  analytics: PropTypes.object.isRequired,
  basePath: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  logout: PropTypes.func,
};

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  linkContainer: {
    textAlign: 'center',
    marginTop: '30px',
  },
  link: {
    ...Theme.link(Colors.rootOrange()),
  },
});

export default withRouter(LoginEmail);
